import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import ReactTooltip from 'react-tooltip';
import { KpiCardsDashboardDto } from 'api/dashboard/dtos/KpiCardsDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import MoneyFormat from 'common/components/moneyFormat/MoneyFormat';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import Utils from 'common/services/Utils';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useWidgetFilters from '../hooks/useWidgetFilters';
import { useDashboardVehicleScope, vehicleScopeCriteria } from '../hooks/useDashboardVehicleScope';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import styles from './KpiCards.module.scss';
import Box from 'common/components/box/Box';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';

type Variant = 'activity' | 'costs';

type Props = {
	variant?: Variant;
	// Legacy props (direct data passing)
	data?: KpiCardsDashboardDto | null;
	isLoading?: boolean;
	// New props (self-fetch mode)
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const VEHICLES_FILTERS_CACHE_KEY = 'VEHICLESFILTER';
const VEHICLES_MANAGEMENT_FILTERS_CACHE_KEY = 'VEHICLESMANAGEMENTFILTER';

const KpiCards = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();

	const navigateToVehicles = (filtersOverride?: Record<string, any>) => {
		try {
			// inactives: false sempre — o dashboard só agrega viaturas ativas, o clique
			// tem de levar à listagem com o mesmo critério para os totais baterem.
			const next = { ...(filtersOverride ?? {}), inactives: false };
			localStorage.setItem(VEHICLES_FILTERS_CACHE_KEY, JSON.stringify(next));
		} catch { /* silent */ }
		window.open('/vehicles', '_blank');
	};

	const [fetchedData, setFetchedData] = useState<KpiCardsDashboardDto | null>(null);
	const [fetchLoading, setFetchLoading] = useState(false);

	const isLegacyMode = props.data !== undefined;
	const variant = props.variant ?? 'activity';
	const { dateFrom, dateTo, setDateRange } = useWidgetFilters(variant === 'costs' ? 'kpiCardsCosts' : 'kpiCardsActivity');
	const scopeVehicleId = useDashboardVehicleScope();
	const vehicleScope = vehicleScopeCriteria(scopeVehicleId);
	// No detalhe do ativo, "TCO médio por ativo" é redundante com o TCO → esconder; tooltip sem "da frota".
	const costsTooltipKey = scopeVehicleId ? 'dashboard.kpi_cards_costs_asset_tooltip' : 'dashboard.kpi_cards_costs_tooltip';

	const navigateToVehiclesManagement = () => {
		try {
			// inactives: false para alinhar com o que o KPI mostra. O dashboard calcula
			// o TCO só para viaturas ativas (default), por isso o clique tem de levar
			// à listagem com o mesmo critério — caso contrário a soma na listagem inclui
			// veículos inativos e dá um total maior que o do KPI.
			const next = {
				costsStartDate: dateFrom,
				costsEndDate: dateTo,
				inactives: false,
			};
			localStorage.setItem(VEHICLES_MANAGEMENT_FILTERS_CACHE_KEY, JSON.stringify(next));
		} catch { /* silent */ }
		window.open('/vehicles-management', '_blank');
	};

	useEffect(() => {
		if (isLegacyMode) return;
		let cancelled = false;
		const fetchData = async () => {
			setFetchLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: variant === 'costs'
						? { dateFrom, dateTo, ...vehicleScope }
						: { ...vehicleScope };
				const result = await DashboardService.getKpiCards(criteria);
				if (!cancelled) {
					setFetchedData(result);
				}
			} catch {
				if (!cancelled) {
					addToast(t('common.messages.error_load_info'), { appearance: 'error' });
				}
			} finally {
				if (!cancelled) {
					setFetchLoading(false);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, isLegacyMode, dateFrom, dateTo]);

	const data = isLegacyMode ? props.data! : fetchedData;
	const isLoading = isLegacyMode ? props.isLoading : fetchLoading;

	const d = data ?? { numberVehicles: 0, costTotalAccumulated: 0, costTotalMoyenne: 0, costPerKm: 0, immobilizedVehicles: 0, expiringReminders: 0, expiringContracts: 0 };

	if (variant === 'activity') {
		if (isLoading) {
			return (
				<Box className={tableStyles.container} title={t('dashboard.fleet_counters') as string} subTitleTooltip={t('dashboard.fleet_counters_tooltip' as any) as string}>
					<div className={styles.centerAlign}><LoadingSpinner /></div>
				</Box>
			);
		}

		const availabilityPercent = d.numberVehicles > 0
			? Math.round(((d.numberVehicles - d.immobilizedVehicles) / d.numberVehicles) * 100)
			: 0;

		return (
			<Box className={tableStyles.container} title={t('dashboard.fleet_counters') as string} subTitleTooltip={t('dashboard.fleet_counters_tooltip' as any) as string}>
				<div className={styles.kpiRow}>
					<div className={`${styles.kpiItem} ${styles.kpiItemClickable}`} onClick={() => navigateToVehicles()}>
						<div className={styles.kpiInline}>
							<div className={styles.kpiLabel}>{t('dashboard.kpi.total_vehicles')}</div>
							<div className={`${styles.kpiValue} ${styles.teal}`}>{d.numberVehicles}</div>
						</div>
					</div>
					<div className={`${styles.kpiItem} ${styles.kpiItemClickable}`} onClick={() => navigateToVehicles({ immobilized: true })}>
						<div className={styles.kpiInline}>
							<div className={styles.kpiLabel}>{t('dashboard.kpi.immobilized')}</div>
							<div className={`${styles.kpiValue} ${styles.amber}`}>{d.immobilizedVehicles}</div>
						</div>
					</div>
					<div className={styles.kpiItem}>
						<div className={styles.kpiInline}>
							<div className={styles.kpiLabel}>{t('dashboard.kpi.availability')}</div>
							<div className={`${styles.kpiValue} ${styles.green}`}>{availabilityPercent}%</div>
						</div>
					</div>
				</div>
			</Box>
		);
	}

	// Costs variant
	const costAvgPerVehicle = d.numberVehicles > 0 ? d.costTotalAccumulated / d.numberVehicles : 0;

	if (isLoading) {
		return (
			<Box className={tableStyles.container} title={t('dashboard.kpi_cards_costs') as string}
				subTitleTooltip={t(costsTooltipKey as any) as string}
				renderOptions={!isLegacyMode ? <WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} /> : undefined}>
				<div className={styles.centerAlign}>
					<LoadingSpinner />
				</div>
			</Box>
		);
	}

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.kpi_cards_costs') as string}
			subTitleTooltip={t(costsTooltipKey as any) as string}
			renderOptions={!isLegacyMode ? <WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} /> : undefined}
		>
			<div className={styles.kpiRow}>
				<div className={`${styles.kpiItem} ${styles.kpiItemClickable}`} onClick={navigateToVehiclesManagement}>
					<div className={styles.kpiInline}>
						<div className={styles.kpiLabel}>{t('dashboard.kpi.total_cost_tco' as any)}</div>
						<div className={`${styles.kpiValue} ${styles.teal}`}>
							<MoneyFormat value={d.costTotalAccumulated} fixedDecimalScale={true} decimalScale={2} />
						</div>
					</div>
				</div>
				{!scopeVehicleId && (
					<>
						<div
							className={`${styles.kpiItem} ${styles.kpiItemClickable}`}
							onClick={navigateToVehiclesManagement}
							data-tip={t('dashboard.kpi.tco_per_vehicle_tooltip' as any, {
								tco: Utils.formatCurrency(d.costTotalAccumulated),
								count: d.numberVehicles,
								result: Utils.formatCurrency(costAvgPerVehicle),
							}) as string}
							data-for="kpi-tco-per-vehicle-tooltip"
						>
							<div className={styles.kpiInline}>
								<div className={styles.kpiLabel}>{t('dashboard.kpi.tco_per_vehicle' as any)}</div>
								<div className={styles.kpiValue}>
									<MoneyFormat value={costAvgPerVehicle} fixedDecimalScale={true} decimalScale={2} />
								</div>
							</div>
						</div>
						<ReactTooltip id="kpi-tco-per-vehicle-tooltip" effect="solid" />
					</>
				)}
				<div
					className={`${styles.kpiItem} ${styles.kpiItemClickable}`}
					onClick={navigateToVehiclesManagement}
					data-tip={t('dashboard.kpi.avg_monthly_cost_tooltip' as any) as string}
					data-for="kpi-avg-monthly-cost-tooltip"
				>
					<div className={styles.kpiInline}>
						<div className={styles.kpiLabel}>{t('dashboard.kpi.avg_monthly_cost' as any)}</div>
						<div className={`${styles.kpiValue} ${styles.blue}`}>
							<MoneyFormat value={d.costTotalMoyenne} fixedDecimalScale={true} decimalScale={2} />
						</div>
					</div>
				</div>
				<ReactTooltip id="kpi-avg-monthly-cost-tooltip" effect="solid" multiline={true} />
				<div
					className={`${styles.kpiItem} ${styles.kpiItemClickable}`}
					onClick={navigateToVehiclesManagement}
					data-tip={t('dashboard.kpi.cost_per_km_tooltip' as any) as string}
					data-for="kpi-cost-per-km-tooltip"
				>
					<div className={styles.kpiInline}>
						<div className={styles.kpiLabel}>{t('dashboard.kpi.cost_per_km_label' as any)}</div>
						<div className={`${styles.kpiValue} ${styles.purple}`}>
							<MoneyFormat value={d.costPerKm} fixedDecimalScale={true} decimalScale={2} suffix={'\u20AC/km'} />
						</div>
					</div>
				</div>
				<ReactTooltip id="kpi-cost-per-km-tooltip" effect="solid" multiline={true} />
			</div>
		</Box>
	);
};

// eslint-disable-next-line
const KpiCardsActivity = (props: any) => <KpiCards {...props} variant="activity" />;
// eslint-disable-next-line
const KpiCardsCosts = (props: any) => <KpiCards {...props} variant="costs" />;

export { KpiCardsActivity, KpiCardsCosts };
export default KpiCards;
